import { gql } from "@apollo/client";
import type {
  Permit,
  PermitStatus,
  PermitApplicationStatus,
  PermitDocumentReviewStatus,
  ISODateTime,
  PermitPayerAccount,
  PermitSubmittedByUser,
} from "common/types/permits";
import type { PermitDetailRaw, RegisteredVehicleSummary } from "common/graphql/fragments";
import { permitDetailFragment } from "../fragments/permits/permitDetailFragment";

export const GET_PERMIT_DETAIL = gql`
  query GetPermitDetail($id: uuid!) {
    permit: permit_by_pk(id: $id) {
      ...permitDetailFragment
    }
  }
  ${permitDetailFragment}
`;

export interface GetPermitDetailVars {
  id: string;
}

export interface GetPermitDetailData {
  permit: PermitDetailRaw | null;
}

interface GetPermitDetailRaw {
  id: string;
  clientId: string;
  displayId: string;
  status: PermitStatus;
  applicationStatus: PermitApplicationStatus;
  validFrom: ISODateTime | null;
  validTo: ISODateTime | null;
  maxNumVehicles: number | null;
  applicationSubmittedAt: ISODateTime | null;
  applicationReviewedAt: ISODateTime | null;
  index: number;
  createdAt: ISODateTime;
  updatedAt: ISODateTime;
  payerAccount: PermitPayerAccount | null;
  permitType: {
    id: string;
    name: string;
    description: string;
  };
  applicationSubmittedByUser: PermitSubmittedByUser | null;
  applicationApprovedByUser: {
    id: string;
    email: string;
    fullName?: string;
  } | null;
  vehicles: {
    permitId: string;
    registeredVehicleId: string;
    accountId: string | null;
    registeredVehicle: RegisteredVehicleSummary;
  }[];
  documentProposals: {
    id: string;
    permitId: string;
    submittedAt: ISODateTime;
    reviewComment: string | null;
    reviewStatus: PermitDocumentReviewStatus;
    reviewedAt: ISODateTime | null;
    createdAt: ISODateTime;
    updatedAt: ISODateTime;
    documents: {
      documentProposalId: string;
      documentId: string;
      reviewStatus: PermitDocumentReviewStatus;
      reviewComment: string | null;
      approvedAt: ISODateTime | null;
      createdAt: ISODateTime;
      updatedAt: ISODateTime;
      document: {
        id: string;
        documentTypeId: string;
        documentType: {
          id: string;
          name: string;
          description: string | null;
        };
        fileName: string;
        s3Bucket: string;
        s3Path: string;
        validFrom: ISODateTime | null;
        validTo: ISODateTime | null;
        createdAt: ISODateTime;
      };
    }[];
  }[];
}

/** Transform raw GraphQL response to Permit type for PermitDetailDialog */
export function transformPermitDetailRaw(raw: GetPermitDetailRaw): Permit {
  return {
    id: raw.id,
    clientId: raw.clientId,
    displayId: raw.displayId,
    status: raw.status,
    applicationStatus: raw.applicationStatus,
    validFrom: raw.validFrom,
    validTo: raw.validTo,
    permitTypeId: raw.permitType.id,
    permitType: raw.permitType as Permit["permitType"],
    payerAccountId: raw.payerAccount?.id ?? null,
    payerAccount: raw.payerAccount ?? undefined,
    payeeAccountId: "", // Not needed for display
    maxNumVehicles: raw.maxNumVehicles,
    applicationSubmittedAt: raw.applicationSubmittedAt,
    applicationSubmittedByUserId: raw.applicationSubmittedByUser?.id ?? null,
    applicationSubmittedByUser: raw.applicationSubmittedByUser ?? undefined,
    applicationReviewedAt: raw.applicationReviewedAt,
    applicationApprovedByUserId: raw.applicationApprovedByUser?.id ?? null,
    applicationApprovedByUser: raw.applicationApprovedByUser ?? undefined,
    index: raw.index,
    createdAt: raw.createdAt,
    updatedAt: raw.updatedAt,
    vehicles: raw.vehicles.map((v) => ({
      permitId: v.permitId,
      registeredVehicleId: v.registeredVehicleId,
      accountId: v.accountId,
      registeredVehicle: v.registeredVehicle,
    })),
    documentProposals: raw.documentProposals.map((dp) => ({
      id: dp.id,
      permitId: dp.permitId,
      submittedByUserId: "", // Not needed for display
      submittedAt: dp.submittedAt,
      reviewComment: dp.reviewComment,
      reviewStatus: dp.reviewStatus,
      reviewedByUserId: null,
      reviewedAt: dp.reviewedAt,
      createdAt: dp.createdAt,
      updatedAt: dp.updatedAt,
      documents: dp.documents.map((doc) => ({
        documentProposalId: doc.documentProposalId,
        documentId: doc.documentId,
        reviewStatus: doc.reviewStatus,
        reviewComment: doc.reviewComment,
        reviewedByUserId: null,
        approvedAt: doc.approvedAt,
        createdAt: doc.createdAt,
        updatedAt: doc.updatedAt,
        document: {
          id: doc.document.id,
          documentTypeId: doc.document.documentTypeId,
          documentType: doc.document.documentType,
          fileName: doc.document.fileName,
          s3Bucket: doc.document.s3Bucket,
          s3Path: doc.document.s3Path,
          uploadedByUserId: "",
          uploadedByAccountId: "",
          validFrom: doc.document.validFrom,
          validTo: doc.document.validTo,
          createdAt: doc.document.createdAt,
        },
      })),
    })),
  };
}
